import { nanoid } from 'nanoid'

import { IFindTicketForm } from 'entities/ticket/models/find-ticket/models'

const AVIA_SEARCH_KEY = 'mtravel_avia_search'

interface AviaSearchState {
  id: string
  signature: string
}

let currentSearch: AviaSearchState | null = null

const getSearchSignature = (form: IFindTicketForm) => {
  const directions = form.directions
    .flatMap((direction) => [
      direction.from?.cityIataCode,
      direction.to?.cityIataCode,
      direction.date,
      direction.back_date,
    ])
    .filter(Boolean)

  return [
    ...directions,
    `A${form.adult || 0}`,
    `C${form.child || 0}`,
    `I${form.infant || 0}`,
    `IP${form.infants_with_place || 0}`,
    form.cabin_class || 'UNKNOWN',
  ]
    .join('_')
    .toUpperCase()
}

const readSearch = (): AviaSearchState | null => {
  try {
    const storedSearch = sessionStorage.getItem(AVIA_SEARCH_KEY)

    if (!storedSearch) return currentSearch

    const parsedSearch = JSON.parse(storedSearch) as AviaSearchState

    if (!parsedSearch.id || !parsedSearch.signature) return currentSearch

    currentSearch = parsedSearch
  } catch {
    // Keep using the in-memory state when sessionStorage is unavailable.
  }

  return currentSearch
}

const saveSearch = (search: AviaSearchState) => {
  currentSearch = search

  try {
    sessionStorage.setItem(AVIA_SEARCH_KEY, JSON.stringify(search))
  } catch {
    // The current runtime can still reuse the in-memory state.
  }
}

const startAviaSearch = (form: IFindTicketForm) => {
  const signature = getSearchSignature(form)
  const id = `${signature}_${nanoid(4).toLowerCase()}`

  saveSearch({ id, signature })

  return id
}

export const getOrCreateAviaSearchId = (form: IFindTicketForm) => {
  const signature = getSearchSignature(form)
  const savedSearch = readSearch()

  if (savedSearch?.signature === signature) return savedSearch.id

  return startAviaSearch(form)
}
